// frontend/src/components/common/ErrorBoundary.jsx
import { Component } from "react";
import Button from "./Button";
import "./ui.css";

/**
 * Catches render errors in its subtree.
 * Props: fallback (node or (error, reset) => node), onReset, onError, children
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.props.onError?.(error, info);
    if (import.meta.env.DEV) console.error("[PAWNI] Render error:", error, info);
  }

  reset() {
    this.setState({ error: null });
    this.props.onReset?.();
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const { fallback } = this.props;
    if (typeof fallback === "function") return fallback(error, this.reset);
    if (fallback) return fallback;

    return (
      <div className="error-fallback" role="alert">
        <p className="eyebrow">Something broke</p>
        <h2 className="error-fallback__title">This part of the page failed to load.</h2>
        <p className="error-fallback__desc">
          Try again. If it keeps happening, reload the page or write to us at{" "}
          <a href="mailto:thepawniproject@gmail.com">thepawniproject@gmail.com</a>.
        </p>
        <div className="error-fallback__actions">
          <Button variant="primary" onClick={this.reset}>Try again</Button>
          <Button variant="ghost" onClick={() => window.location.reload()}>Reload page</Button>
        </div>
      </div>
    );
  }
}
